
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject, 
  StorageReference,
  getStorage
} from "firebase/storage";
import { uploadBytes } from "firebase/storage";
import { app, auth } from "./firebaseService";
import { handleFirebaseError } from "../utils/errorUtils";

let _storage: ReturnType<typeof getStorage> | null = null;
function storageForApp() {
  if (_storage) return _storage;
  _storage = getStorage(app);
  return _storage;
}

// Define TypeScript type for better parameter clarity
export type UploadProgressCallback = (progress: number) => void;
export type UploadErrorCallback = (error: Error) => void;
export type UploadCompleteCallback = (url: string, path: string) => void;

/**
 * Upload a photo to Firebase storage
 * @param file The file to upload
 * @param orgId The organization ID to use in the storage path
 * @param onProgress Optional callback for upload progress
 * @param onError Optional callback for error handling
 * @param onComplete Optional callback when upload completes
 */
export const uploadPhoto = async (
  file: File,
  orgId: string,
  onProgress?: UploadProgressCallback,
  onError?: UploadErrorCallback,
  onComplete?: UploadCompleteCallback
) => {
  console.log("uploadPhoto called with file:", file?.name, "orgId:", orgId);
  
  if (!file) {
    const error = new Error("No file provided for upload");
    console.error(error);
    if (onError) onError(error);
    return null;
  }
  
  if (!orgId) {
    const error = new Error("No organization ID provided for upload");
    console.error(error);
    if (onError) onError(error);
    return null;
  }
  
  // Check if user is authenticated - this is crucial
  const currentUser = auth.currentUser;
  console.log("Firebase auth check - currentUser:", !!currentUser, currentUser?.uid);
  
  if (!currentUser) {
    const error = new Error("User must be authenticated to upload files");
    console.error(error);
    if (onError) onError(error);
    return null;
  }
  
  try {
    // Get a fresh token for this operation
    await auth.currentUser?.reload();
    
    // Force auth token refresh before uploading
    // Get the id token with forced refresh
    const idToken = await currentUser.getIdToken(true);
    console.log("Authentication token refreshed successfully:", !!idToken);
    
    // Also get token result to inspect claims (for debugging)
    const idTokenResult = await currentUser.getIdTokenResult();
    console.log("Token refreshed before upload, claims:", idTokenResult.claims);
    const locationAccessGroupId =
      typeof (idTokenResult.claims as any)?.locationAccessGroupId === "string"
        ? ((idTokenResult.claims as any).locationAccessGroupId as string)
        : null;
    
    // Create a unique file name
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
    const filePath = `org/${orgId}/${fileName}`;
    const storageRef = ref(storageForApp(), filePath);
    
    console.log("Uploading to path:", filePath);
    console.log("User authenticated:", !!currentUser, "UID:", currentUser.uid);
    console.log("Organization ID for storage path:", orgId);
    console.log("Role in token:", idTokenResult.claims.role);
    
    // Set upload metadata to include auth token
    const metadata = {
      contentType: file.type,
      customMetadata: {
        // Include user ID in metadata for storage rules
        uploadedBy: currentUser.uid,
        ...(locationAccessGroupId ? { locationAccessGroupId } : {})
      }
    };

    // Upload the file with metadata
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    // Listen for state changes, errors, and completion
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Upload progress:", progress);
        if (onProgress) onProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        
        // Get more detailed error information
        if (error.code === 'storage/unauthorized') {
          console.error("User doesn't have permission to upload to this location");
          
          // Log authentication details
          currentUser.getIdTokenResult().then(idTokenResult => {
            console.error("Token claims during error:", idTokenResult.claims);
            console.error("Token expiration:", new Date(idTokenResult.expirationTime));
          });
        }
        
        // Convert Firebase error to user-friendly message
        const friendlyError = new Error(handleFirebaseError(error));
        
        if (onError) onError(friendlyError);
      },
      () => {
        // Upload completed successfully, get download URL
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          console.log("Upload complete, download URL:", downloadURL);
          if (onComplete) onComplete(downloadURL, filePath);
        });
      }
    );
      
    return true; // We're handling the upload async now
  } catch (error) {
    console.error("Error setting up upload:", error);
    
    // Convert Firebase error to user-friendly message
    const friendlyError = new Error(handleFirebaseError(error));
    
    if (onError) onError(friendlyError);
    return null;
  }
};

export const deletePhotoFromStorage = async (path: string) => {
  // Check if user is authenticated
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("User must be authenticated to delete files");
  }
  
  try {
    // Make sure we have a fresh authentication session
    await auth.currentUser?.reload();
    
    // Force auth token refresh before deleting
    await currentUser.getIdToken(true);
    const tokenResult = await currentUser.getIdTokenResult();
    console.log("Token claims before deleting photo:", tokenResult.claims);
    
    const storageRef = ref(storageForApp(), path);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    console.error("Error deleting file from storage:", error);
    throw error;
  }
};

export const getPhotoUrl = async (path: string) => {
  try {
    // Ensure auth is refreshed
    const currentUser = auth.currentUser;
    if (currentUser) {
      await currentUser.reload();
      await currentUser.getIdToken(true);
    }
    
    const storageRef = ref(storageForApp(), path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error getting download URL:", error);
    throw error;
  }
};

/**
 * Copy an existing storage object into a new file under org/{orgId}/..., returning the new path and URL.
 * This runs client-side: fetches the source blob via signed URL, then re-uploads.
 */
export const copyPhotoFile = async (sourcePath: string, orgId: string): Promise<{ path: string; url: string }> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User must be authenticated to copy files");

  // Refresh auth
  await currentUser.reload();
  await currentUser.getIdToken(true);
  const tokenResult = await currentUser.getIdTokenResult();
  const locationAccessGroupId =
    typeof (tokenResult.claims as any)?.locationAccessGroupId === "string"
      ? ((tokenResult.claims as any).locationAccessGroupId as string)
      : null;

  const sourceRef = ref(storageForApp(), sourcePath);
  const sourceUrl = await getDownloadURL(sourceRef);

  const resp = await fetch(sourceUrl);
  if (!resp.ok) throw new Error(`Failed to fetch source image: ${resp.status}`);
  const blob = await resp.blob();

  const timestamp = Date.now();
  const base = sourcePath.split("/").pop() || "photo.jpg";
  const fileName = `${timestamp}_${base.replace(/[^a-zA-Z0-9.]/g, "_")}`;
  const newPath = `org/${orgId}/${fileName}`;
  const destRef = ref(storageForApp(), newPath);

  await uploadBytes(destRef, blob, {
    contentType: blob.type || "image/jpeg",
    customMetadata: {
      uploadedBy: currentUser.uid,
      sourcePath,
      ...(locationAccessGroupId ? { locationAccessGroupId } : {})
    }
  });
  const url = await getDownloadURL(destRef);
  return { path: newPath, url };
};
